/**
 * TalentOS — candidate match report ("Detailed Analysis").
 *
 * Drops into a modal body and renders one candidate's scoring for one job.
 * Self-contained: it fetches its own data from `jobId` + `resumeId`, so a host
 * screen only has to know those two ids.
 *
 * Rebuilt 2026-09-23 to match the approved "Detailed Analysis" design
 * (link shared in chat) — pure TalentOS data throughout, no Foundry fields
 * mixed in. Every field rendered here traces to a real column on
 * talentos_match_runs/talentos_match_dimension_scores/talentos_jd_analyses/
 * talentos_resume_analyses; see talentos/matching/api.py's response model
 * docstrings for the exact source of each one. Nothing here is invented —
 * a field TalentOS has no opinion on renders as an em dash or is omitted,
 * never a guess.
 */
import React, { useState } from 'react'
import { toast } from 'react-hot-toast'
import { ArrowDownTrayIcon, CheckIcon, ExclamationTriangleIcon, XMarkIcon as XIcon } from '@heroicons/react/24/outline'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import { BriefcaseIcon, BuildingOffice2Icon, MapPinIcon } from '@heroicons/react/24/outline'
import MarkdownRenderer from '../common/MarkdownRenderer'
import StatusChangeCommentModal from '../matching/StatusChangeCommentModal'
import { useCandidateStages } from '../../hooks/useCandidateStages'
import { useTalentosMatch } from './useTalentosMatch'
import { downloadTalentosMatchReportPdf } from './downloadMatchReportPdf'
import type {
  TalentosMatchRun,
  TalentosResponsibilityComparison,
  TalentosSkillComparison,
} from './talentosApi'
import {
  dimensionLabel,
  experiencePillClasses,
  experienceStatusLabel,
  experienceStatusSymbol,
  gateOutcomeLabel,
  gateOutcomePillClasses,
  gateOutcomeSymbol,
  hasGateData,
  NEUTRAL_PILL_CLASSES,
  NEUTRAL_PILL_SYMBOL,
  percentText,
  recommendationLabel,
  recommendationPillClasses,
  recommendationPillSymbol,
  recommendationRingColor,
  responsibilityLabelText,
  skillCategoryLabel,
  skillVerdictClasses,
  skillVerdictLabel,
  skillVerdictSymbol,
  verdictDecidedByGate,
} from './talentosLabels'

/** One row of the "JD vs Résumé Comparison" table. The table mixes several
 *  very different row kinds (skill, mandatory item, experience, seniority,
 *  domain, responsibility) that share the same six columns but never the
 *  same "Match" cell shape, so each kind renders into this common shape up
 *  front rather than the table trying to branch on row type internally. */
interface ComparisonRow {
  key: string
  requirement: string
  category: string
  jdRequirement: React.ReactNode
  resume: React.ReactNode
  /** Candidate's years of experience specific to this requirement, from the
   *  résumé (TalentosSkillComparison.years_of_experience) — distinct from
   *  jdRequirement's minimum_years (what the JD asked for) and from the
   *  "Total experience" row's resume column (the candidate's overall
   *  experience, not scoped to this one skill). 'N/A' for row kinds that
   *  aren't skill-scoped (experience/seniority). */
  relevantYears: React.ReactNode
  match: React.ReactNode
  evidence: React.ReactNode
}

function skillComparisonRow(row: TalentosSkillComparison): ComparisonRow {
  return {
    key: `skill-${row.category}-${row.skill}`,
    requirement: row.skill,
    category: skillCategoryLabel(row.category),
    jdRequirement: (
      <>
        {row.category === 'required' ? 'Required' : 'Preferred'}
        {row.minimum_years != null && ` · ${row.minimum_years}+ yrs`}
      </>
    ),
    resume: row.verdict === 'not_found' ? 'N/A' : row.skill,
    relevantYears: row.years_of_experience != null ? `${row.years_of_experience} yrs` : 'N/A',
    match: (
      <Pill className={skillVerdictClasses(row.verdict)}>
        {skillVerdictSymbol(row.verdict)} {skillVerdictLabel(row.verdict)}
      </Pill>
    ),
    evidence: row.evidence,
  }
}

/** "Domain fit" — collapsible card in the new "Domain & Responsibilities"
 *  subsection (2026-09-24 design update; replaces the old in-table Domain
 *  row). Deliberately no score badge on the collapsed header, matching the
 *  approved design exactly -- the JD-vs-résumé domain text is the headline,
 *  the LLM's own reasoning is one click away. */
function DomainFitCard({
  jd,
  resume,
  evidence,
  open,
  onToggle,
}: {
  jd: string
  resume: string
  evidence: string
  open: boolean
  onToggle: () => void
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start gap-3.5 px-3.5 py-3 text-left hover:bg-gray-50"
      >
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-gray-900">Domain fit</div>
          {/* JD and Resume stacked on their own lines, each label a fixed
              width so the values themselves line up in a column. */}
          <div className="mt-1.5 flex flex-col gap-1 text-sm text-gray-700">
            <span className="flex items-baseline gap-2">
              <span className="w-16 flex-shrink-0 text-gray-500">JD:</span>
              <b className="font-semibold text-gray-900">{jd}</b>
            </span>
            <span className="flex items-baseline gap-2">
              <span className="w-16 flex-shrink-0 text-gray-500">Resume:</span>
              <b className="font-semibold text-gray-900">{resume}</b>
            </span>
          </div>
        </div>
        <span className="mt-0.5 flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-blue-600">
          {open ? 'Hide details' : 'View details'}
          <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div className="border-t border-gray-100 bg-[#fbfdff] px-3.5 py-3.5">
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-gray-400">Evidence</div>
          <div className="text-sm leading-relaxed text-gray-700 text-pretty">{evidence}</div>
        </div>
      )}
    </div>
  )
}

/** "Responsibilities" — collapsible list in the same subsection, one row per
 *  JD responsibility. Replaces the old in-table Responsibility rows. Each
 *  item's collapsed state truncates to one line; expanded shows the JD text,
 *  the quoted résumé citation TalentOS actually found (or none), and
 *  TalentOS's own stored label verbatim -- never an invented percentage
 *  (see `responsibilityLabelText`; a prior version of this section showed
 *  "Aligned 100/50/0%", which the data does not actually support). */
function ResponsibilitiesCard({
  items,
  openIndexes,
  onToggle,
}: {
  items: TalentosResponsibilityComparison[]
  openIndexes: Record<number, boolean>
  onToggle: (index: number) => void
}) {
  const counts = new Map<string, number>()
  for (const item of items) {
    const label = responsibilityLabelText(item.label)
    counts.set(label, (counts.get(label) ?? 0) + 1)
  }
  const statusSummary = Array.from(counts.entries())
    .map(([label, count]) => `${count} ${label.toLowerCase()}`)
    .join(' · ')

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center gap-2.5 bg-gray-50 px-3.5 py-2.5">
        <span className="text-sm font-bold text-gray-900">Responsibilities</span>
        <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs font-semibold text-gray-600">
          {items.length}
        </span>
        <span className="text-xs text-gray-500">{statusSummary}</span>
      </div>
      {items.map((item, index) => {
        const open = !!openIndexes[index]
        return (
          <div key={index} className="border-t border-gray-100">
            <button
              type="button"
              onClick={() => onToggle(index)}
              className="flex w-full items-center gap-3 px-3.5 py-2.5 text-left hover:bg-gray-50"
            >
              <span className="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full bg-blue-50 text-[11px] font-bold text-blue-700">
                {index + 1}
              </span>
              <span
                className={`min-w-0 flex-1 text-sm font-semibold text-gray-900 ${open ? '' : 'truncate'}`}
              >
                {item.responsibility}
              </span>
              <span className="flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-blue-600">
                {open ? 'Hide' : 'View details'}
                <ChevronDownIcon className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
              </span>
            </button>
            {open && (
              <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-3.5 px-3.5 pb-3.5 pl-[48px]">
                <div>
                  <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                    JD Responsibility
                  </div>
                  <div className="text-sm leading-relaxed text-gray-900 text-pretty">{item.responsibility}</div>
                </div>
                <div>
                  <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-gray-400">Resume</div>
                  <div className="text-sm leading-relaxed text-gray-700 text-pretty">{item.citation ?? '—'}</div>
                </div>
                <div>
                  <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-gray-400">Evidence</div>
                  <div className="text-sm leading-relaxed text-gray-700 text-pretty">
                    {responsibilityLabelText(item.label)}
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

interface TalentosMatchReportProps {
  jobId: string
  resumeId: string
  /** Shown while loading and if TalentOS parsed no name of its own. Pass the
   *  name the host already has so the header never flashes empty. */
  fallbackCandidateName?: string
  /** Rendered when this candidate has no TalentOS run. Lets a host screen keep
   *  its own report as a transitional fallback instead of showing a dead end. */
  renderNotScored?: () => React.ReactNode
  /** Called when "Close" is pressed — the report itself no longer assumes an
   *  X button lives in a parent's own modal chrome. Optional: a host that
   *  still renders its own close control can leave this unset. */
  onClose?: () => void
  /** Wires the Approve/Deny buttons to an actual status change — the exact
   *  same signature as `ApplicationStatusDropdown`'s own `onStatusChange`
   *  (`(candidateStatusId, comment) => ...`), so a host that already has that
   *  handler (see `JobDetailPage.tsx`'s `handleApplicationStatusChange`) can
   *  pass it straight through. Left unset, Approve/Deny stay inert — matching
   *  every host until this is wired up. */
  onStatusChange?: (candidateStatusId: string, comment: string) => void | Promise<void>
  /** The candidate's current `candidate_status_id` (the same value the host's
   *  own candidate list/status dropdown already tracks) — lets Approve/Deny
   *  recognise a decision that's already been made and show its outcome
   *  instead of the still-actionable buttons. Optional: omitted, the buttons
   *  always render actionable (matching every host until this is wired up). */
  currentStatusId?: string | null
}

// ── small presentational pieces ─────────────────────────────────────────────

function NumberedSection({
  number,
  title,
  subtitle,
  defaultOpen = true,
  children,
}: {
  number: number
  title: string
  subtitle?: string
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-3 px-5 py-4 text-left"
        aria-expanded={open}
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-700">
            {number}
          </span>
          <div>
            <h3 className="text-[15px] font-bold text-slate-900">{title}</h3>
            {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
          </div>
        </div>
        <span className="mt-0.5 flex-shrink-0 text-slate-500">
          {open ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
        </span>
      </button>
      {open && <div className="border-t border-gray-100 px-5 py-4">{children}</div>}
    </div>
  )
}

/** 6px radius solid pill — NOT the fully-rounded `rounded-full` shape this
 *  used to have. Matches the design's status-pill style exactly (tight
 *  padding, semibold 12px text, no ring/border). */
function Pill({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${className}`}
    >
      {children}
    </span>
  )
}

/** Simple SVG ring — no charting library needed for one static arc. Color
 *  follows the recommendation's own semantic color (same mapping as its
 *  pill), quietened to gray when a gate — not the score — decided the
 *  verdict, or when there is no score at all. */
function ScoreRing({ percent, ringColor }: { percent: number | null; ringColor: string }) {
  const radius = 34
  const circumference = 2 * Math.PI * radius
  const fraction = percent === null ? 0 : Math.max(0, Math.min(100, percent)) / 100
  const dash = circumference * fraction
  return (
    <div className="relative h-[88px] w-[88px] flex-shrink-0">
      <svg viewBox="0 0 80 80" className="h-[88px] w-[88px] -rotate-90" role="img" aria-label="Match score">
        <circle cx="40" cy="40" r={radius} fill="none" stroke="#eef2f7" strokeWidth="8" />
        {percent !== null && (
          <circle
            cx="40"
            cy="40"
            r={radius}
            fill="none"
            stroke={ringColor}
            strokeWidth="8"
            strokeDasharray={`${dash} ${circumference}`}
            strokeLinecap="round"
          />
        )}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[21px] font-extrabold text-slate-900">{percentText(percent)}%</span>
      </div>
    </div>
  )
}

function ComparisonLegendDot({ colorClass, label }: { colorClass: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-[0.7rem] text-gray-500">
      <span className={`h-1.5 w-1.5 rounded-full ${colorClass}`} />
      {label}
    </span>
  )
}

function ComparisonTable({ rows }: { rows: ComparisonRow[] }) {
  if (rows.length === 0) {
    return <p className="text-sm text-gray-500">No JD requirements to compare for this run.</p>
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-xs font-semibold text-gray-500">
            <th className="pb-2 pr-3">Requirement Skill(s)</th>
            <th className="pb-2 pr-3">Category</th>
            <th className="pb-2 pr-3">Resume</th>
            <th className="pb-2 pr-3">Relevant Years</th>
            <th className="pb-2 pr-3">Match</th>
            <th className="pb-2">Evidence</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.key}>
              <td className="py-2 pr-3 font-medium text-gray-900">{row.requirement}</td>
              <td className="py-2 pr-3 text-gray-600">{row.category}</td>
              <td className="py-2 pr-3 text-gray-600">{row.resume}</td>
              <td className="py-2 pr-3 text-gray-600">{row.relevantYears}</td>
              <td className="py-2 pr-3">{row.match}</td>
              <td className="py-2 text-gray-500">{row.evidence}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── the report ──────────────────────────────────────────────────────────────

function ReportBody({
  run,
  fallbackName,
}: {
  run: TalentosMatchRun
  fallbackName?: string
}) {
  const name = run.candidate.full_name || fallbackName || 'Candidate'
  const scoreOverridden = verdictDecidedByGate(run)
  const showGates = run.gate_status !== 'ineligible' && hasGateData(run)
  const requiredCoverage = run.skills_threshold_evidence
  const preferredCoverage = run.preferred_skills_evidence
  const skillRows = run.skill_comparison ?? []

  const experienceRow: ComparisonRow | null = run.job
    ? {
        key: 'experience-total',
        requirement: 'Total experience',
        category: 'Experience',
        jdRequirement: (() => {
          const min = run.job?.min_experience_years
          const max = run.job?.max_experience_years
          if (min != null && max != null) return `${min}-${max} years`
          if (min != null) return `${min}+ years`
          if (max != null) return `Up to ${max} years`
          return 'N/A'
        })(),
        resume:
          run.candidate.total_experience_years != null
            ? `${run.candidate.total_experience_years.toFixed(1)} years`
            : 'N/A',
        relevantYears: 'N/A',
        match: (
          <Pill className={experiencePillClasses(run.gate_status)}>
            {experienceStatusSymbol(run.gate_status)} {experienceStatusLabel(run.gate_status)}
          </Pill>
        ),
        evidence: run.gate_reason ?? 'N/A',
      }
    : null

  const seniorityRow: ComparisonRow | null = run.job
    ? {
        key: 'experience-seniority',
        requirement: 'Seniority',
        category: 'Experience',
        jdRequirement: run.job.seniority_level ?? 'N/A',
        resume: run.candidate.current_title ?? 'N/A',
        relevantYears: 'N/A',
        match: <Pill className={NEUTRAL_PILL_CLASSES}>{NEUTRAL_PILL_SYMBOL} Informational</Pill>,
        evidence: 'Current title from résumé',
      }
    : null

  // Education (2026-10-08) -- JD side is real data, wired through, but
  // genuinely empty on every run today: no JD-analysis extraction writes
  // talentos_jd_analyses.education_requirements yet (see that column's own
  // docstring). Shown anyway, honestly N/A, rather than hiding the JD side
  // -- it'll start populating the moment that extraction exists, with no
  // further change needed here. Résumé side is real and already populated
  // (education_summary, the same field the Resume Profile tab shows).
  // Informational, not qualify/disqualify, same reasoning as Seniority
  // above: there is no gate that actually evaluates this (education_gate_
  // status sits at its 'skipped' backfill default on every run).
  const educationRequirements = run.job?.education_requirements ?? []
  const educationSummary = run.candidate.education_summary ?? []
  const hasEducationCard = educationRequirements.length > 0 || educationSummary.length > 0
  const educationRow: ComparisonRow | null = hasEducationCard
    ? {
        key: 'education',
        requirement: 'Education',
        category: 'Education',
        jdRequirement:
          educationRequirements.length > 0 ? (
            <div className="space-y-0.5">
              {educationRequirements.map((e, i) => (
                <div key={i}>
                  {e.minimum_degree_level ?? 'N/A'}
                  {e.preferred_fields_of_study && e.preferred_fields_of_study.length > 0
                    ? ` — ${e.preferred_fields_of_study.join(', ')}`
                    : ''}
                </div>
              ))}
            </div>
          ) : (
            'N/A'
          ),
        resume:
          educationSummary.length > 0 ? (
            <div className="space-y-0.5">
              {educationSummary.map((e, i) => (
                <div key={i}>{e}</div>
              ))}
            </div>
          ) : (
            'N/A'
          ),
        relevantYears: 'N/A',
        match: <Pill className={NEUTRAL_PILL_CLASSES}>{NEUTRAL_PILL_SYMBOL} Informational</Pill>,
        // No gate VERDICT exists for education (see this row's own comment
        // above), but each JD requirement does carry a real `rationale` --
        // TalentOS's own reasoning for why it extracted that requirement --
        // so that's shown here instead of a blank N/A. Still 'N/A' when
        // there's nothing to show (JD side empty, or every item lacks one).
        evidence:
          educationRequirements.filter((e) => e.rationale).length > 0 ? (
            <div className="space-y-0.5">
              {educationRequirements
                .filter((e) => e.rationale)
                .map((e, i) => (
                  <div key={i}>{e.rationale}</div>
                ))}
            </div>
          ) : (
            'N/A'
          ),
      }
    : null

  // ── "Domain & Responsibilities" subsection (2026-09-24 design update) --
  // pulled out of the main comparison table into its own collapsible cards
  // below it. Domain fit's evidence text is the dimension's own reasoning;
  // no score badge on the collapsed header, matching the approved design.
  const domainDim = run.dimensions.find((d) => d.dimension_key === 'domain')
  const candidateDomain = run.candidate.domain ?? []
  const hasDomainCard = !!(run.job?.domain || candidateDomain.length > 0)
  const responsibilityItems = run.responsibility_comparison ?? []
  const [domainOpen, setDomainOpen] = useState(false)
  const [respOpenIndexes, setRespOpenIndexes] = useState<Record<number, boolean>>({})

  // Total experience/Seniority used to be two rows at the bottom of the
  // Skills Matrix table — pulled out into their own "Experience" cards
  // section instead (per request), so the table now holds only actual
  // skill rows.
  const comparisonRows: ComparisonRow[] = skillRows.map(skillComparisonRow)

  const scored = run.dimensions.filter((d) => d.weight_applied > 0)

  return (
    <div className="flex flex-col gap-4">
      {/* ── identity header ── */}
      <div className="rounded-xl border border-[#eef2f7] bg-white p-[18px_20px]">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] items-center gap-[18px]">
          <div className="min-w-0">
            <h2 className="text-[19px] font-bold text-slate-900 text-balance">{name}</h2>
            {run.candidate.current_title && (
              <div className="mt-[3px] text-[13px] text-slate-500">{run.candidate.current_title}</div>
            )}
            <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-slate-600">
              {run.candidate.current_company && (
                <span className="flex items-center gap-1.5">
                  <BuildingOffice2Icon className="h-3.5 w-3.5 text-slate-500" />
                  {run.candidate.current_company}
                </span>
              )}
              {run.candidate.total_experience_years != null && (
                <span className="flex items-center gap-1.5">
                  <BriefcaseIcon className="h-3.5 w-3.5 text-slate-500" />
                  {run.candidate.total_experience_years.toFixed(1)} years
                </span>
              )}
              {run.candidate.location && (
                <span className="flex items-center gap-1.5">
                  <MapPinIcon className="h-3.5 w-3.5 text-slate-500" />
                  {run.candidate.location}
                </span>
              )}
            </div>
          </div>

          {run.job && (run.job.role_title || run.job.seniority_level || run.job.domain) && (
            <div className="min-w-0 border-l border-[#eef2f7] pl-[18px]">
              <div className="text-xs text-slate-500">Job Title</div>
              {run.job.role_title && (
                <div className="mt-[3px] mb-2 text-[14.5px] font-bold text-slate-900">{run.job.role_title}</div>
              )}
              <div className="flex flex-wrap gap-2">
                {run.job.seniority_level && (
                  <span className="rounded-md bg-slate-100 px-2.5 py-1 text-[11.5px] font-medium text-slate-600 capitalize">
                    {run.job.seniority_level}
                  </span>
                )}
                {run.job.domain && (
                  <span className="rounded-md bg-slate-100 px-2.5 py-1 text-[11.5px] font-medium text-slate-600">
                    {run.job.domain}
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 border-l border-[#eef2f7] pl-[18px]">
            <ScoreRing
              percent={run.display_percent}
              ringColor={
                run.display_percent === null || scoreOverridden
                  ? '#d1d5db'
                  : recommendationRingColor(run.recommendation, run.gate_status)
              }
            />
            <div className="min-w-0">
              <Pill className={recommendationPillClasses(run.recommendation, run.gate_status)}>
                {recommendationPillSymbol(run.recommendation, run.gate_status)}{' '}
                {recommendationLabel(run.recommendation, run.gate_status)}
              </Pill>
              {requiredCoverage && (
                <p className="mt-[7px] text-xs leading-normal text-slate-500">
                  {requiredCoverage.matched_count} of {requiredCoverage.jd_skill_count} required
                  skills matched
                  {requiredCoverage.missing_skills.length > 0 &&
                    `; ${requiredCoverage.missing_skills.length} not found in the resume.`}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── 1. Summary ── */}
      {run.narrative_summary && (
        <NumberedSection number={1} title="Summary">
          <div className="rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-700">
            <MarkdownRenderer content={run.narrative_summary} className="prose-p:my-0" />
          </div>
        </NumberedSection>
      )}

      {/* ── 2. Skills Matrix — table, Experience, and Domain &
          Responsibilities all merged into this one section, per request. */}
      {(comparisonRows.length > 0 || experienceRow || seniorityRow || hasDomainCard || responsibilityItems.length > 0) && (
        <NumberedSection
          number={2}
          title="Skills Matrix"
          subtitle="Comparison between job requirements and candidate's resume"
        >
          {comparisonRows.length > 0 && (
            <>
              <div className="mb-3 flex flex-wrap justify-end gap-3">
                <ComparisonLegendDot colorClass="bg-emerald-500" label="Matched" />
                <ComparisonLegendDot colorClass="bg-amber-500" label="Partial" />
                <ComparisonLegendDot colorClass="bg-red-500" label="Not found" />
                <ComparisonLegendDot colorClass="bg-blue-500" label="Insufficient evidence" />
              </div>
              <ComparisonTable rows={comparisonRows} />
            </>
          )}

          {(experienceRow || seniorityRow) && (
            <div className="mt-[18px]">
              <h5 className="mb-3 text-sm font-bold text-gray-900">Experience</h5>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {experienceRow && (
                  <div className="rounded-lg border border-gray-200 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <h4 className="font-semibold text-gray-900">{experienceRow.requirement}</h4>
                      {experienceRow.match}
                    </div>
                    <div className="mt-3">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Resume</div>
                      <div className="mt-0.5 text-sm text-gray-900">{experienceRow.resume}</div>
                    </div>
                    <p className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500"><span className="font-medium">Evidence :-</span> {experienceRow.evidence}</p>
                  </div>
                )}
                {seniorityRow && (
                  <div className="rounded-lg border border-gray-200 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <h4 className="font-semibold text-gray-900">{seniorityRow.requirement}</h4>
                      {seniorityRow.match}
                    </div>
                    <div className="mt-3">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Resume</div>
                      <div className="mt-0.5 text-sm text-gray-900">{seniorityRow.resume}</div>
                    </div>
                    <p className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500"><span className="font-medium">Evidence :-</span> {seniorityRow.evidence}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {(hasDomainCard || responsibilityItems.length > 0) && (
            <div className="mt-[18px] flex flex-col gap-3.5">
              <h5 className="text-sm font-bold text-gray-900">Domain &amp; Responsibilities</h5>
              {hasDomainCard && (
                <DomainFitCard
                  jd={run.job?.domain ?? 'N/A'}
                  resume={candidateDomain.length > 0 ? candidateDomain.join(', ') : 'N/A'}
                  evidence={domainDim?.reasoning ?? 'N/A'}
                  open={domainOpen}
                  onToggle={() => setDomainOpen((v) => !v)}
                />
              )}
              {responsibilityItems.length > 0 && (
                <ResponsibilitiesCard
                  items={responsibilityItems}
                  openIndexes={respOpenIndexes}
                  onToggle={(index) => setRespOpenIndexes((prev) => ({ ...prev, [index]: !prev[index] }))}
                />
              )}
            </div>
          )}
        </NumberedSection>
      )}

      {/* ── 3. Education ── */}
      {educationRow && (
        <NumberedSection number={3} title="Education">
          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-start justify-between gap-3">
              <h4 className="font-semibold text-gray-900">{educationRow.requirement}</h4>
              {educationRow.match}
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">JD</div>
                <div className="mt-0.5 text-sm text-gray-900">{educationRow.jdRequirement}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Resume</div>
                <div className="mt-0.5 text-sm text-gray-900">{educationRow.resume}</div>
              </div>
            </div>
            <p className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500"><span className="font-medium">Evidence :-</span> {educationRow.evidence}</p>
          </div>
        </NumberedSection>
      )}

      {/* ── 4. Match Result ── */}
      {scored.length > 0 && (
        <NumberedSection number={4} title="Match Result">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-semibold text-gray-500">
                  <th className="pb-2 pr-3">Dimension</th>
                  <th className="pb-2 pr-3">Score</th>
                  <th className="pb-2 pr-3">Weight</th>
                  <th className="pb-2">Key insight</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {scored.map((dim) => {
                  const percent = Math.round(dim.raw_score * 100)
                  return (
                    <tr key={dim.dimension_key}>
                      <td className="py-2 pr-3 font-medium text-gray-900 whitespace-nowrap">
                        {dimensionLabel(dim.dimension_key)}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs">{percent}%</span>
                          <span className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100">
                            <span
                              className="block h-full rounded-full bg-blue-600"
                              style={{ width: `${percent}%` }}
                            />
                          </span>
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">
                        {Math.round(dim.weight_applied * 100)}%
                      </td>
                      <td className="py-2 text-gray-600">
                        <MarkdownRenderer
                          content={dim.display_reasoning ?? dim.reasoning ?? ''}
                          className="text-xs prose-p:my-0 prose-ul:my-0 prose-ul:pl-4 prose-li:my-0"
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </NumberedSection>
      )}

      {/*
      ── 5. Gate Details — hidden per request ──
      <NumberedSection
        number={5}
        title="Gate Details"
      >
        <div className="flex flex-col gap-3">
          Experience Gate — always shown, it's the first gate to run.
          <div className="rounded-lg border border-gray-200 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 text-[0.65rem] font-bold text-gray-500">
                  1
                </span>
                <h4 className="font-semibold text-gray-900">Experience Gate</h4>
              </div>
              <Pill className={experiencePillClasses(run.gate_status)}>
                {experienceStatusSymbol(run.gate_status)} {experienceStatusLabel(run.gate_status)}
              </Pill>
            </div>
            {run.gate_reason && <p className="mt-2 text-sm font-medium text-gray-900">{run.gate_reason}</p>}
          </div>

          {showGates && (
            <>
              Required-Skills Threshold Gate
              <div className="rounded-lg border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 text-[0.65rem] font-bold text-gray-500">
                      2
                    </span>
                    <h4 className="font-semibold text-gray-900">Required-Skills Threshold Gate</h4>
                  </div>
                  <Pill className={gateOutcomePillClasses(run.skills_threshold_status)}>
                    {gateOutcomeSymbol(run.skills_threshold_status)} {gateOutcomeLabel(run.skills_threshold_status)}
                  </Pill>
                </div>
                {requiredCoverage && (
                  <>
                    <p className="mt-2 text-sm font-medium text-gray-900">
                      {requiredCoverage.matched_count} of {requiredCoverage.jd_skill_count} required
                      skills evidenced ({Math.round(requiredCoverage.coverage * 100)}%)
                      {run.skills_threshold_status === 'failed' &&
                        ` — below the ${Math.round(requiredCoverage.threshold * 100)}% threshold.`}
                    </p>
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-100">
                      <span
                        className={`block h-full rounded-full ${
                          run.skills_threshold_status === 'failed' ? 'bg-red-500' : 'bg-emerald-500'
                        }`}
                        style={{ width: `${Math.round(requiredCoverage.coverage * 100)}%` }}
                      />
                    </div>
                    {requiredCoverage.missing_skills.length > 0 && (
                      <p className="mt-2 text-xs text-gray-500">
                        <span className="font-medium">Missing:</span>{' '}
                        {requiredCoverage.missing_skills.join(', ')}
                      </p>
                    )}
                  </>
                )}
              </div>

              {preferredCoverage && preferredCoverage.jd_skill_count > 0 && (
                <p className="text-xs text-gray-400">
                  Preferred skills: {preferredCoverage.matched_count} of{' '}
                  {preferredCoverage.jd_skill_count} evidenced — informational only, no gate.
                </p>
              )}
            </>
          )}
        </div>
      </NumberedSection>
      */}

    </div>
  )
}

/**
 * The action bar (Approve/Deny/Download/Close) -- deliberately a SEPARATE
 * component from `ReportBody`, rendered by the host as a sibling OUTSIDE the
 * scrollable body region (see `TalentosMatchReportFooter` below and
 * `JobDetailPage.tsx`'s modal), not with `position: sticky` inside it.
 * Sticky was tried first (2026-09-24) and produced an intermittent visual
 * overlap with in-flow table content at certain scroll positions -- moving
 * the bar structurally outside the scrolling container removes the
 * possibility of that glitch entirely, the same fix already applied to the
 * header.
 */
function ActionBar({
  run,
  jobId,
  resumeId,
  fallbackName,
  onClose,
  onStatusChange,
  currentStatusId,
}: {
  run: TalentosMatchRun
  jobId: string
  resumeId: string
  fallbackName?: string
  onClose?: () => void
  onStatusChange?: (candidateStatusId: string, comment: string) => void | Promise<void>
  currentStatusId?: string | null
}) {
  const [downloading, setDownloading] = useState(false)
  const name = run.candidate.full_name || fallbackName || 'Candidate'

  // Approve/Deny -- both resolve to the "Resume Screening" stage's own
  // Accepted/Rejected sub-statuses (seeded 2026-09-23, sitting between
  // "Submitted" and "Screening"). Looked up by code from the same master
  // list `ApplicationStatusDropdown` uses, never hardcoded ids -- a status's
  // id is only known once it exists in a given environment's seeded data.
  const { flatStatuses } = useCandidateStages()
  const resumeScreeningAccepted = flatStatuses.find((s) => s.statusCode === 'RESUME_SCREENING_ACCEPTED')
  const resumeScreeningRejected = flatStatuses.find((s) => s.statusCode === 'RESUME_SCREENING_REJECTED')
  const [pendingDecision, setPendingDecision] = useState<'approve' | 'deny' | null>(null)
  const pendingStatus = pendingDecision === 'approve' ? resumeScreeningAccepted : resumeScreeningRejected
  // Once a decision is already reflected in the candidate's real status,
  // the buttons give way to a plain outcome pill -- clicking Approve again
  // on an already-approved candidate isn't a real action.
  const isApproved = !!resumeScreeningAccepted && currentStatusId === resumeScreeningAccepted.id
  const isDenied = !!resumeScreeningRejected && currentStatusId === resumeScreeningRejected.id

  const handleDownload = async () => {
    if (downloading) return
    setDownloading(true)
    try {
      const downloaded = await downloadTalentosMatchReportPdf(jobId, resumeId, name)
      // false means this candidate has no TalentOS run yet -- there's
      // nothing to build a report from.
      if (!downloaded) {
        toast.error('This candidate has not been scored by TalentOS yet.')
      }
    } catch (error) {
      console.error('Failed to generate the match report:', error)
      toast.error('Failed to generate the report. Please try again.')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-5 py-4">
        <div className="flex items-center gap-2">
          {isApproved || isDenied ? (
            // Already decided -- the buttons would just re-open the same
            // decision, so the outcome replaces them instead of sitting
            // beside still-actionable controls.
            <Pill className={isApproved ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
              {isApproved ? '✓ Approved' : '✕ Denied'} :{' '}
              {isApproved
                ? (resumeScreeningAccepted && resumeScreeningAccepted.stageName)
                : (resumeScreeningRejected && resumeScreeningRejected.stageName)}
            </Pill>
          ) : (
            <>
              {/* Sets the candidate's status to Resume Screening: Accepted/
                  Rejected, via a mandatory-reason popup (2026-09-23) -- the
                  reason lands on the audit trail through the same
                  application-status endpoint ApplicationStatusDropdown uses. */}
              <button
                type="button"
                onClick={() => setPendingDecision('approve')}
                disabled={!onStatusChange || !resumeScreeningAccepted}
                title={
                  !onStatusChange || !resumeScreeningAccepted
                    ? 'Resume Screening status is not available here'
                    : undefined
                }
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <CheckIcon className="h-4 w-4" />
                Approve
              </button>
              <button
                type="button"
                onClick={() => setPendingDecision('deny')}
                disabled={!onStatusChange || !resumeScreeningRejected}
                title={
                  !onStatusChange || !resumeScreeningRejected
                    ? 'Resume Screening status is not available here'
                    : undefined
                }
                className="inline-flex items-center gap-1.5 rounded-md border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <XIcon className="h-4 w-4" />
                Deny
              </button>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {downloading ? 'Preparing…' : 'Download'}
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
            >
              Close
            </button>
          )}
        </div>
      </div>

      {pendingDecision && pendingStatus && onStatusChange && (
        <StatusChangeCommentModal
          status={pendingStatus}
          commentRequired
          onCancel={() => setPendingDecision(null)}
          onConfirm={(comment) => Promise.resolve(onStatusChange(pendingStatus.id, comment))}
          onSuccess={() => setPendingDecision(null)}
        />
      )}
    </>
  )
}

function useReportQuery(jobId: string, resumeId: string) {
  return useTalentosMatch(jobId, resumeId)
}

function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-3 animate-pulse" aria-busy="true">
      <div className="h-8 w-1/2 mx-auto rounded bg-gray-200" />
      <div className="h-28 rounded-xl bg-gray-100" />
      <div className="h-40 rounded-xl bg-gray-100" />
    </div>
  )
}

function LoadError({ error }: { error: unknown }) {
  return (
    <div className="flex gap-2.5 rounded-lg bg-amber-50 ring-1 ring-amber-200 px-4 py-3">
      <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-600" />
      <div>
        <h4 className="font-semibold text-amber-900">Could not load the TalentOS report</h4>
        <p className="mt-0.5 text-sm text-amber-800">
          {(error as any)?.message ?? 'The scoring service did not respond.'}
        </p>
      </div>
    </div>
  )
}

/**
 * Just the report content (identity header through Gate Details) -- no
 * action bar. Pair with `TalentosMatchReportFooter` when the host wants to
 * render the two as separate flex siblings (header/scrollable-body/footer),
 * so the footer never sits inside the scrolling region -- see
 * `JobDetailPage.tsx`'s modal and `ActionBar`'s own docstring for why.
 */
export function TalentosMatchReportBody({
  jobId,
  resumeId,
  fallbackCandidateName,
  renderNotScored,
}: Pick<TalentosMatchReportProps, 'jobId' | 'resumeId' | 'fallbackCandidateName' | 'renderNotScored'>) {
  const { data, isLoading, isError, error } = useReportQuery(jobId, resumeId)

  if (isLoading) return <LoadingSkeleton />
  if (isError) return <LoadError error={error} />

  // null (not undefined) means the request succeeded and there is no run —
  // ordinary for anyone matched before TalentOS was installed.
  if (data === null) {
    if (renderNotScored) return <>{renderNotScored()}</>
    return (
      <div className="rounded-lg bg-gray-50 px-4 py-6 text-center">
        <h4 className="font-semibold text-gray-900">Not scored by TalentOS yet</h4>
        <p className="mt-1 text-sm text-gray-600">
          Re-run Apply Matching for this candidate to generate a report.
        </p>
      </div>
    )
  }

  return <ReportBody run={data!} fallbackName={fallbackCandidateName} />
}

/**
 * The Approve/Deny/Download/Close bar, as its own component -- see
 * `TalentosMatchReportBody` above and `ActionBar`'s docstring. Renders
 * nothing while loading, on error, or when this candidate has no TalentOS
 * run (nothing to act on) -- same states `TalentosMatchReportBody` shows its
 * own UI for, just silent here since a host rendering both already shows the
 * status once via the Body half.
 */
export function TalentosMatchReportFooter({
  jobId,
  resumeId,
  fallbackCandidateName,
  onClose,
  onStatusChange,
  currentStatusId,
}: Pick<
  TalentosMatchReportProps,
  'jobId' | 'resumeId' | 'fallbackCandidateName' | 'onClose' | 'onStatusChange' | 'currentStatusId'
>) {
  const { data } = useReportQuery(jobId, resumeId)
  if (!data) return null

  return (
    <ActionBar
      run={data}
      jobId={jobId}
      resumeId={resumeId}
      fallbackName={fallbackCandidateName}
      onClose={onClose}
      onStatusChange={onStatusChange}
      currentStatusId={currentStatusId}
    />
  )
}

/** Convenience: Body + Footer together, unsplit -- for a host that just
 *  wants to drop the whole report (including its action bar) in one place
 *  without a separate fixed-footer layout. `TalentosMatchReportBody`/
 *  `TalentosMatchReportFooter` above are the two halves this composes. */
export default function TalentosMatchReport(props: TalentosMatchReportProps) {
  return (
    <>
      <TalentosMatchReportBody {...props} />
      <TalentosMatchReportFooter {...props} />
    </>
  )
}
